<template>
    <div class="text-sm break-words text-foreground">
        <!-- Complex error array display -->
        <div v-if="processedErrorArray && processedErrorArray.length > 0" class="space-y-4">
            <div class="border-l-2 border-purple-500 pl-3 mb-4">
                <h4 class="text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">
                    Complex Error Details ({{ processedErrorArray.length }} errors)
                </h4>
            </div>
            <div v-for="errorItem in processedErrorArray" :key="errorItem.index" class="border border-border rounded-lg overflow-hidden">
                <div class="bg-muted/50 px-3 py-2 border-b flex items-center gap-2">
                    <span class="text-sm font-medium">{{ processedErrorArray.length > 1 ? `Error ${errorItem.index}` : 'Error Details' }}</span>
                    <span v-if="errorItem.isStackTrace" class="text-xs bg-orange-500/20 text-orange-600 dark:text-orange-400 px-2 py-1 rounded"
                        >Stack Trace</span
                    >
                </div>
                <div class="p-3 bg-muted/20">
                    <pre
                        class="font-mono text-xs whitespace-pre-wrap leading-relaxed"
                        v-html="highlightComplexError(errorItem.content, searchTerm)"></pre>
                </div>
            </div>
        </div>

        <!-- Regular JSON display -->
        <div v-else-if="parsedJson" class="space-y-3">
            <div class="border-l-2 border-blue-500 pl-3">
                <h4 class="text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">Structured Message Details</h4>
                <pre class="font-mono text-xs whitespace-pre-wrap" v-html="highlightJson(formattedJson, searchTerm)"></pre>
            </div>
        </div>

        <!-- Validation errors formatted nicely -->
        <div v-else-if="validationErrors.length > 0" class="space-y-3">
            <div class="border-l-2 border-orange-500 pl-3">
                <h4 class="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">Validation Issues</h4>
                <ul class="space-y-2">
                    <li
                        v-for="(error, index) in validationErrors"
                        :key="index"
                        class="flex items-start gap-2 p-2 bg-orange-50 dark:bg-orange-950/50 rounded">
                        <span
                            class="flex-shrink-0 w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold"
                            >{{ index + 1 }}</span
                        >
                        <span class="font-mono text-xs" v-html="highlightText(error, searchTerm)"></span>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Fallback to formatted text -->
        <div v-else class="space-y-2">
            <div class="border-l-2 border-gray-500 pl-3">
                <h4 class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Message Details</h4>
                <div
                    v-for="(line, index) in message.lines"
                    :key="index"
                    class="font-mono text-xs leading-relaxed"
                    v-html="highlightText(line, message.searchTerm)"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    message: {
        type: Object,
        required: true
    },
    searchTerm: {
        type: String,
        default: ''
    }
});

// Try to parse JSON structure
const parsedJson = computed(() => {
    const raw = props.message.raw;

    // For complex errors like "Deployed failed: [array]", manually parse the array
    const complexMatch = raw.match(/^[^:]*:\s*(\[.*\])$/s);
    if (complexMatch) {
        const arrayContent = complexMatch[1];

        // Manually parse the array to handle mixed quotes
        const parsedArray = parseArrayWithMixedQuotes(arrayContent);
        if (parsedArray) {
            return parsedArray;
        }
    }

    // Try to find a JSON array anywhere in the message
    const arrayMatch = raw.match(/(\[.*\])/s);
    if (arrayMatch) {
        // First try standard JSON parsing
        try {
            return JSON.parse(arrayMatch[1]);
        } catch (e) {
            // Try manual parsing for mixed quotes
            const parsedArray = parseArrayWithMixedQuotes(arrayMatch[1]);
            if (parsedArray) {
                return parsedArray;
            }
        }
    }

    // Try to parse as regular JSON
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
});

// Helper function to manually parse arrays with mixed quotes
function parseArrayWithMixedQuotes(arrayStr) {
    try {
        // Remove outer brackets
        const content = arrayStr.slice(1, -1).trim();
        if (!content) return [];

        const items = [];
        let current = '';
        let inString = false;
        let stringChar = null; // Track if we're in ' or " string
        let escapeNext = false;
        let depth = 0;

        for (let i = 0; i < content.length; i++) {
            const char = content[i];

            if (escapeNext) {
                current += char;
                escapeNext = false;
                continue;
            }

            if (char === '\\') {
                escapeNext = true;
                current += char;
                continue;
            }

            if (!inString) {
                if (char === '"' || char === "'") {
                    inString = true;
                    stringChar = char;
                    current += char;
                } else if (char === '[' || char === '{') {
                    depth++;
                    current += char;
                } else if (char === ']' || char === '}') {
                    depth--;
                    current += char;
                } else if (char === ',' && depth === 0) {
                    // End of current item
                    const trimmed = current.trim();
                    if (trimmed) {
                        items.push(parseStringItem(trimmed));
                    }
                    current = '';
                } else {
                    current += char;
                }
            } else {
                current += char;
                if (char === stringChar) {
                    inString = false;
                    stringChar = null;
                }
            }
        }

        // Add the last item
        const trimmed = current.trim();
        if (trimmed) {
            items.push(parseStringItem(trimmed));
        }

        return items;
    } catch (e) {
        console.error('Error parsing array with mixed quotes:', e);
        return null;
    }
}

// Helper to parse individual string items
function parseStringItem(item) {
    const trimmed = item.trim();

    // Handle quoted strings
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        // Remove quotes and unescape
        let content = trimmed.slice(1, -1);

        // Unescape common escape sequences
        content = content
            .replace(/\\"/g, '"')
            .replace(/\\'/g, "'")
            .replace(/\\\\/g, '\\')
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\r/g, '\r');

        return content;
    }

    // Handle other types (numbers, booleans, etc.)
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);

    // Default to string
    return trimmed;
}

// Process complex error arrays
const processedErrorArray = computed(() => {
    if (!Array.isArray(parsedJson.value)) {
        return null;
    }

    return parsedJson.value.map((item, index) => {
        if (typeof item === 'string') {
            // Unescape newlines and process as formatted text
            const unescaped = item.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '\r').replace(/\\\\/g, '\\'); // Unescape escaped backslashes

            return {
                index: index + 1,
                content: unescaped,
                isStackTrace: /at\s+.+\(|Exception:|Error:|\.java:|\.scala:|\.py:|\.js:|\.ts:/.test(unescaped),
                lines: unescaped.split('\n')
            };
        }
        return {
            index: index + 1,
            content: JSON.stringify(item, null, 2),
            isStackTrace: false,
            lines: JSON.stringify(item, null, 2).split('\n')
        };
    });
});

const formattedJson = computed(() => {
    if (!parsedJson.value) return '';
    return JSON.stringify(parsedJson.value, null, 2);
});

// Extract validation errors
const validationErrors = computed(() => {
    const errors = [];
    const text = props.message.raw;

    // Common validation error patterns
    const patterns = [
        /field .+ is required/gi,
        /invalid .+ format/gi,
        /must be .+ characters/gi,
        /does not match pattern/gi,
        /.+ is not a valid .+/gi,
        /.+ must be .+/gi
    ];

    patterns.forEach((pattern) => {
        const matches = text.match(pattern);
        if (matches) {
            errors.push(...matches);
        }
    });

    // If no specific patterns found, try to extract numbered errors
    if (errors.length === 0) {
        const numberedErrors = text.match(/^\d+\.\s*.+/gm);
        if (numberedErrors) {
            errors.push(...numberedErrors);
        }
    }

    return [...new Set(errors)]; // Remove duplicates
});

function highlightJson(jsonString, searchTerm) {
    let highlighted = jsonString;

    // Highlight search term
    if (searchTerm) {
        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 text-black dark:text-white">$1</mark>');
    }

    // JSON syntax highlighting
    highlighted = highlighted
        // String values
        .replace(/"([^"]+)":/g, '<span class="text-blue-600 dark:text-blue-400 font-medium">"$1"</span>:')
        // String values (values, not keys)
        .replace(/:\s*"([^"]+)"/g, ': <span class="text-green-600 dark:text-green-400">"$1"</span>')
        // Numbers
        .replace(/:\s*(\d+\.?\d*)/g, ': <span class="text-purple-600 dark:text-purple-400">$1</span>')
        // Booleans
        .replace(/:\s*(true|false)/g, ': <span class="text-orange-600 dark:text-orange-400">$1</span>')
        // null
        .replace(/:\s*(null)/g, ': <span class="text-gray-500 dark:text-gray-400">$1</span>');

    return highlighted;
}

function highlightText(text, searchTerm) {
    let highlighted = text;

    if (searchTerm) {
        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 text-black dark:text-white">$1</mark>');
    }

    return highlighted;
}

function highlightComplexError(text, searchTerm) {
    let highlighted = text;

    // Highlight search term first
    if (searchTerm) {
        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 text-black dark:text-white">$1</mark>');
    }

    // Apply specialized highlighting for complex errors
    highlighted = highlighted
        // Exception types and errors
        .replace(/(\w+Exception|Error:)/g, '<span class="text-red-600 dark:text-red-400 font-semibold">$1</span>')

        // Stack trace frames
        .replace(/(at\s+[\w.$]+\([^)]+\))/g, '<span class="text-blue-600 dark:text-blue-400">$1</span>')

        // File paths and line numbers
        .replace(/([\w/.-]+\.(java|scala|py|js|ts|go|rs|cpp|c):\d+)/g, '<span class="text-green-600 dark:text-green-400 underline">$1</span>')

        // Class names and packages
        .replace(/((?:org|com|java|scala)\.[a-zA-Z.]+)/g, '<span class="text-purple-600 dark:text-purple-400">$1</span>')

        // Log levels
        .replace(/\b(ERROR|WARN|INFO|DEBUG|FATAL|TRACE)\b/g, '<span class="font-bold $1">$1</span>')
        .replace(/class="font-bold ERROR"/, 'class="font-bold text-red-600 dark:text-red-400"')
        .replace(/class="font-bold WARN"/, 'class="font-bold text-yellow-600 dark:text-yellow-400"')
        .replace(/class="font-bold INFO"/, 'class="font-bold text-blue-600 dark:text-blue-400"')
        .replace(/class="font-bold DEBUG"/, 'class="font-bold text-gray-600 dark:text-gray-400"')
        .replace(/class="font-bold FATAL"/, 'class="font-bold text-red-800 dark:text-red-300"')

        // Timestamps
        .replace(/(\d{2}:\d{2}:\d{2}[.,]\d{3})/g, '<span class="text-gray-500 dark:text-gray-400">$1</span>')

        // Thread names in brackets
        .replace(/\[([^\]]+)\]/g, '<span class="text-cyan-600 dark:text-cyan-400">[$1]</span>')

        // Caused by chains
        .replace(/(Caused by:)/g, '<span class="text-orange-600 dark:text-orange-400 font-medium">$1</span>')

        // Important keywords
        .replace(/\b(failed|crashed|timeout|denied|null|undefined)\b/gi, '<span class="text-red-500 dark:text-red-400 font-medium">$1</span>')

        // Success/completion keywords (if any)
        .replace(/\b(completed|success|finished|done)\b/gi, '<span class="text-green-600 dark:text-green-400 font-medium">$1</span>');

    return highlighted;
}

function escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
</script>
